// @ts-nocheck
import React, { useState, useMemo, useCallback } from 'react';
import {
  Table, Card, Button, Space, message, Row, Col, Statistic,
  Typography, Upload, Tag, Modal, Radio, Alert, Steps, Select,
} from 'antd';
import {
  UploadOutlined, DownloadOutlined, CheckCircleOutlined,
  WarningOutlined, CloseCircleOutlined, SwapOutlined, FileExcelOutlined,
  InboxOutlined, ArrowLeftOutlined, CheckOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type { UploadFile, RcFile } from 'antd/es/upload/interface';
import { Title } from '../../components/design-system/typography';
import { colors, spacing } from '../../components/design-system/tokens';
import dayjs from 'dayjs';

const { Text } = Typography;
const { Dragger } = Upload;
const { Step } = Steps;
const { Option } = Select;

type DiscrepancyType = 'conflict' | 'ss_only' | 'db_only';

interface Discrepancy {
  field: string;
  spreadsheet: string;
  database: string;
  portcast: string | null;
  selectedSource?: 'spreadsheet' | 'database' | 'portcast';
  type: DiscrepancyType;
  rejected?: boolean;
}

interface ReconciliationRow {
  ourNo: string;
  rimtradeRef: string;
  buyer: string;
  supplier: string;
  contractNo: string;
  cDate: string;
  price: string;
  etdSs: string;
  etdDb: string;
  etdPc: string;
  etaSs: string;
  etaDb: string;
  etaPc: string;
  vesselSs: string;
  vesselDb: string;
  destination: string;
  status: string;
  discrepancies: Discrepancy[];
  matchMethod: string;
  shipmentId: string | null;
  bookmarkId: string | null;
  startSs: string;
  startDb: string;
  endSs: string;
  endDb: string;
}

type SpreadsheetFormat = 'almond' | 'walnut';

interface ReconciliationResult {
  rows: ReconciliationRow[];
  summary: {
    totalProcessed: number;
    foundInDatabase: number;
    notFoundInDatabase: number;
    withDiscrepancies: number;
    withPortcastData: number;
    matchRate: number;
    oneSidedUpdates: number;
  };
  format: SpreadsheetFormat;
}

interface ColumnMapping {
  ourNo: number;
  packer: number;
  broker: number | null;
  contractNo: number;
  cDate: number;
  size: number;
  prices: number;
  quantity: number;
  etd: number;
  eta: number;
  destination: number;
  vessel: number;
  status: number | null;
  shipment: number | null;
}

interface SpreadsheetPreview {
  headers: string[];
  sampleRows: string[][];
  detectedFormat: 'almond' | 'walnut';
  suggestedMapping: ColumnMapping;
  headerRowIndex: number;
  totalDataRows: number;
}

type StepType = 'upload' | 'mapping' | 'results';

const FIELD_OPTIONS: { value: keyof ColumnMapping; label: string }[] = [
  { value: 'ourNo', label: 'Our No.' },
  { value: 'packer', label: 'Packer' },
  { value: 'broker', label: 'Broker' },
  { value: 'contractNo', label: 'Contract No.' },
  { value: 'cDate', label: 'Contract Date' },
  { value: 'size', label: 'Size/Spec' },
  { value: 'prices', label: 'Price' },
  { value: 'quantity', label: 'Quantity' },
  { value: 'etd', label: 'ETD' },
  { value: 'eta', label: 'ETA' },
  { value: 'destination', label: 'Destination' },
  { value: 'vessel', label: 'Vessel' },
  { value: 'status', label: 'Status' },
  { value: 'shipment', label: 'Shipment Period' },
];

const ShinyeiReconciliation: React.FC = () => {
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [currentStep, setCurrentStep] = useState<StepType>('upload');
  const [previewData, setPreviewData] = useState<SpreadsheetPreview | null>(null);
  const [columnMapping, setColumnMapping] = useState<ColumnMapping | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [applying, setApplying] = useState(false);
  const [result, setResult] = useState<ReconciliationResult | null>(null);
  const [selectedResolutions, setSelectedResolutions] = useState<
    Map<string, Map<string, 'spreadsheet' | 'database' | 'portcast'>>
  >(new Map());
  const [discrepancyModalRow, setDiscrepancyModalRow] = useState<ReconciliationRow | null>(null);

  const getToken = () => localStorage.getItem('atlas_token') || localStorage.getItem('auth_token');

  const handlePreview = useCallback(async () => {
    if (fileList.length === 0) {
      message.warning('Please select an XLSX file first');
      return;
    }

    const file = fileList[0] as RcFile;
    if (file.size > 10 * 1024 * 1024) {
      message.error('File exceeds 10MB limit');
      return;
    }

    setPreviewing(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch('/api/v1/shinyei-reconciliation/preview', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getToken()}` },
        body: formData,
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({ error: 'Preview failed' }));
        throw new Error(err.error || err.message || 'Preview failed');
      }

      const data: SpreadsheetPreview = await response.json();
      setPreviewData(data);
      setColumnMapping(data.suggestedMapping);
      setCurrentStep('mapping');
      message.success('Preview loaded. Please review and confirm column mappings.');
    } catch (error: any) {
      message.error(error.message || 'Failed to preview file');
    } finally {
      setPreviewing(false);
    }
  }, [fileList]);

  const handleConfirmMapping = useCallback(async () => {
    if (fileList.length === 0 || !columnMapping) {
      message.warning('No file or mapping available');
      return;
    }

    if (columnMapping.ourNo === null || columnMapping.ourNo === undefined) {
      message.warning('Please map the "Our No." column (required)');
      return;
    }

    const file = fileList[0] as RcFile;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('columnMapping', JSON.stringify(columnMapping));

      const response = await fetch('/api/v1/shinyei-reconciliation/upload', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getToken()}` },
        body: formData,
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({ error: 'Upload failed' }));
        throw new Error(err.error || err.message || 'Upload failed');
      }

      const data: ReconciliationResult = await response.json();
      setResult(data);
      setSelectedResolutions(new Map());
      setCurrentStep('results');
      message.success(`Processed ${data.summary.totalProcessed} rows. Match rate: ${data.summary.matchRate}%`);
    } catch (error: any) {
      message.error(error.message || 'Failed to process file');
    } finally {
      setUploading(false);
    }
  }, [fileList, columnMapping]);

  const handleBackToMapping = useCallback(() => {
    setCurrentStep('mapping');
    setResult(null);
  }, []);

  const handleBackToUpload = useCallback(() => {
    setCurrentStep('upload');
    setPreviewData(null);
    setColumnMapping(null);
    setFileList([]);
  }, []);

  const handleExport = useCallback(async () => {
    if (!result) return;
    setExporting(true);
    try {
      const response = await fetch('/api/v1/shinyei-reconciliation/export', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ result }),
      });

      if (!response.ok) throw new Error('Export failed');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `shinyei-reconciliation-${dayjs().format('YYYY-MM-DD')}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      message.success('Report exported successfully');
    } catch {
      message.error('Failed to export report');
    } finally {
      setExporting(false);
    }
  }, [result]);

  const handleApply = useCallback(async () => {
    const updates: Array<{ shipmentId: string; field: string; value: string; source: string }> = [];

    selectedResolutions.forEach((fieldMap, ourNo) => {
      const row = result?.rows.find(r => r.ourNo === ourNo);
      if (!row?.shipmentId) return;

      fieldMap.forEach((source, field) => {
        const disc = row.discrepancies.find(d => d.field === field);
        if (!disc) return;

        let value = '';
        if (source === 'spreadsheet') value = disc.spreadsheet;
        else if (source === 'database') value = disc.database;
        else if (source === 'portcast' && disc.portcast) value = disc.portcast;

        if (value) {
          updates.push({ shipmentId: row.shipmentId!, field, value, source });
        }
      });
    });

    if (updates.length === 0) {
      message.warning('No resolutions selected to apply');
      return;
    }

    setApplying(true);
    try {
      const response = await fetch('/api/v1/shinyei-reconciliation/apply', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ updates }),
      });

      if (!response.ok) throw new Error('Apply failed');

      const data = await response.json();
      message.success(`Applied ${data.applied} updates (${data.failed} failed)`);
      setSelectedResolutions(new Map());
    } catch {
      message.error('Failed to apply updates');
    } finally {
      setApplying(false);
    }
  }, [selectedResolutions, result]);

  const setResolution = (ourNo: string, field: string, source: 'spreadsheet' | 'database' | 'portcast') => {
    setSelectedResolutions(prev => {
      const next = new Map(prev);
      if (!next.has(ourNo)) next.set(ourNo, new Map());
      next.get(ourNo)!.set(field, source);
      return next;
    });
  };

  const discrepancyRows = useMemo(() => {
    if (!result) return [];
    return result.rows.filter(r => r.discrepancies.length > 0);
  }, [result]);

  const totalResolutions = useMemo(() => {
    let count = 0;
    selectedResolutions.forEach(fieldMap => { count += fieldMap.size; });
    return count;
  }, [selectedResolutions]);

  const getStatusTag = (status: string) => {
    if (status === 'Matched') return <Tag color="success" icon={<CheckCircleOutlined />}>Matched</Tag>;
    if (status === 'Not Found in Database') return <Tag color="error" icon={<CloseCircleOutlined />}>Not Found</Tag>;
    return <Tag color="warning" icon={<WarningOutlined />}>{status}</Tag>;
  };

  const handleFieldAssignment = (field: keyof ColumnMapping, columnOneBasedIndex: number) => {
    setColumnMapping(prev => {
      if (!prev) return prev;
      const next = { ...prev };
      for (const [key, val] of Object.entries(next)) {
        if (val === columnOneBasedIndex) {
          (next as any)[key] = null;
        }
      }
      next[field] = columnOneBasedIndex;
      return next;
    });
  };

  const handleFieldClear = (columnOneBasedIndex: number) => {
    setColumnMapping(prev => {
      if (!prev) return prev;
      const next = { ...prev };
      for (const [key, val] of Object.entries(next)) {
        if (val === columnOneBasedIndex) {
          (next as any)[key] = null;
        }
      }
      return next;
    });
  };

  const isFieldMapped = (field: keyof ColumnMapping): boolean => {
    return columnMapping ? columnMapping[field] !== null && columnMapping[field] !== undefined : false;
  };

  const getMappedFieldForColumn = (zeroBasedIndex: number): string | null => {
    if (!columnMapping) return null;
    const oneBasedIndex = zeroBasedIndex + 1;
    for (const [field, index] of Object.entries(columnMapping)) {
      if (index === oneBasedIndex) return field;
    }
    return null;
  };

  const columns: ColumnsType<ReconciliationRow> = [
    {
      title: 'Our No.',
      dataIndex: 'ourNo',
      key: 'ourNo',
      width: 130,
      fixed: 'left',
      render: (text: string, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{text}</Text>
          {record.rimtradeRef && <Text type="secondary" style={{ fontSize: 11 }}>{record.rimtradeRef}</Text>}
        </Space>
      ),
    },
    {
      title: 'Supplier',
      dataIndex: 'supplier',
      key: 'supplier',
      width: 80,
    },
    {
      title: 'Buyer',
      dataIndex: 'buyer',
      key: 'buyer',
      width: 80,
    },
    {
      title: 'Contract No.',
      dataIndex: 'contractNo',
      key: 'contractNo',
      width: 140,
    },
    {
      title: 'C Date',
      dataIndex: 'cDate',
      key: 'cDate',
      width: 100,
    },
    {
      title: 'ETD',
      key: 'etd',
      width: 200,
      render: (_: unknown, record: ReconciliationRow) => (
        <ThreeWayCell
          label="ETD"
          ss={record.etdSs}
          db={record.etdDb}
          pc={record.etdPc}
          discrepancy={record.discrepancies.find(d => d.field === 'ETD')}
        />
      ),
    },
    {
      title: 'ETA',
      key: 'eta',
      width: 200,
      render: (_: unknown, record: ReconciliationRow) => (
        <ThreeWayCell
          label="ETA"
          ss={record.etaSs}
          db={record.etaDb}
          pc={record.etaPc}
          discrepancy={record.discrepancies.find(d => d.field === 'ETA')}
        />
      ),
    },
    {
      title: 'Vessel',
      key: 'vessel',
      width: 180,
      render: (_: unknown, record: ReconciliationRow) => (
        <ThreeWayCell
          label="Vessel"
          ss={record.vesselSs}
          db={record.vesselDb}
          pc=""
          discrepancy={record.discrepancies.find(d => d.field === 'Vessel')}
        />
      ),
    },
    {
      title: 'Destination',
      dataIndex: 'destination',
      key: 'destination',
      width: 120,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 140,
      render: (status: string) => getStatusTag(status),
      filters: [
        { text: 'Matched', value: 'Matched' },
        { text: 'Discrepancies', value: 'discrepancy' },
        { text: 'Not Found', value: 'Not Found in Database' },
      ],
      onFilter: (value, record) => {
        if (value === 'discrepancy') return record.discrepancies.length > 0;
        return record.status === value;
      },
    },
    {
      title: 'Resolve',
      key: 'resolve',
      width: 100,
      fixed: 'right',
      render: (_: unknown, record: ReconciliationRow) => {
        if (record.discrepancies.length === 0) return null;
        const resolved = selectedResolutions.get(record.ourNo)?.size || 0;
        return (
          <Button
            size="small"
            type={resolved > 0 ? 'primary' : 'default'}
            icon={<SwapOutlined />}
            onClick={() => setDiscrepancyModalRow(record)}
          >
            {resolved}/{record.discrepancies.length}
          </Button>
        );
      },
    },
  ];

  const getStepIndex = () => {
    switch (currentStep) {
      case 'upload': return 0;
      case 'mapping': return 0;
      case 'results': return 1;
      default: return 0;
    }
  };

  return (
    <div style={{ padding: spacing['2xl'], background: colors.background.primary, minHeight: '100vh' }}>
      <div style={{ maxWidth: 1600, margin: '0 auto' }}>
        <div style={{ marginBottom: spacing.xl, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <Title>Shinyei Reconciliation</Title>
            <Text type="secondary">
              Upload buyer spreadsheet to compare against database and Portcast tracking data
            </Text>
          </div>
          <Space>
            {currentStep === 'results' && (
              <>
                <Button
                  icon={<ArrowLeftOutlined />}
                  onClick={handleBackToMapping}
                >
                  Re-map Columns
                </Button>
                <Button
                  icon={<DownloadOutlined />}
                  onClick={handleExport}
                  loading={exporting}
                >
                  Export Excel
                </Button>
                {totalResolutions > 0 && (
                  <Button
                    type="primary"
                    icon={<CheckCircleOutlined />}
                    onClick={handleApply}
                    loading={applying}
                  >
                    Apply {totalResolutions} Resolution{totalResolutions !== 1 ? 's' : ''}
                  </Button>
                )}
              </>
            )}
          </Space>
        </div>

        <Card style={{ marginBottom: spacing.lg }}>
          <Steps current={getStepIndex()} size="small">
            <Step 
              title="Upload & Map Columns" 
              description="Select file and confirm column mappings"
              icon={currentStep === 'mapping' ? <CheckOutlined /> : undefined}
            />
            <Step 
              title="Review Results" 
              description="Compare data and resolve discrepancies"
            />
          </Steps>
        </Card>

        {currentStep === 'upload' && (
          <Card>
            <Row gutter={[16, 16]} align="middle">
              <Col xs={24}>
                <Space.Compact style={{ width: '100%', maxWidth: 400 }}>
                  <Upload
                    accept=".xlsx,.xls"
                    maxCount={1}
                    fileList={fileList}
                    beforeUpload={(file) => {
                      setFileList([file]);
                      return false;
                    }}
                    onRemove={() => setFileList([])}
                  >
                    <Button icon={<UploadOutlined />}>Select File</Button>
                  </Upload>
                  <Button
                    type="primary"
                    icon={<FileExcelOutlined />}
                    onClick={handlePreview}
                    loading={previewing}
                    disabled={fileList.length === 0}
                  >
                    Preview & Map Columns
                  </Button>
                </Space.Compact>
              </Col>
            </Row>
            <div style={{ marginTop: spacing.lg }}>
              <Dragger
                accept=".xlsx,.xls"
                maxCount={1}
                fileList={fileList}
                beforeUpload={(file) => {
                  if (file.size > 10 * 1024 * 1024) {
                    message.error('File exceeds 10MB limit');
                    return false;
                  }
                  setFileList([file]);
                  return false;
                }}
                onRemove={() => setFileList([])}
                style={{ padding: '40px 0' }}
              >
                <p className="ant-upload-drag-icon">
                  <InboxOutlined style={{ color: colors.primary[60], fontSize: 48 }} />
                </p>
                <p className="ant-upload-text">Click or drag Shinyei XLSX file to this area</p>
                <p className="ant-upload-hint">
                  Supports Almond (13-column) and Walnut (12-column) formats. Max file size: 10MB.
                </p>
              </Dragger>
            </div>
          </Card>
        )}

        {currentStep === 'mapping' && previewData && columnMapping && (
          <Card
            title={
              <Space>
                <Text strong>Column Mapping</Text>
                <Tag color={previewData.detectedFormat === 'almond' ? 'blue' : 'green'}>
                  {previewData.detectedFormat === 'almond' ? 'Almond Format' : 'Walnut Format'}
                </Tag>
                <Tag>{previewData.totalDataRows} data rows</Tag>
              </Space>
            }
            extra={
              <Space>
                <Button
                  icon={<ArrowLeftOutlined />}
                  onClick={handleBackToUpload}
                >
                  Back
                </Button>
                <Button
                  type="primary"
                  icon={<CheckOutlined />}
                  onClick={handleConfirmMapping}
                  loading={uploading}
                  disabled={!isFieldMapped('ourNo')}
                >
                  Confirm & Reconcile
                </Button>
              </Space>
            }
          >
            <Alert
              message="Map spreadsheet columns to system fields"
              description={'Review the detected column mappings below. At minimum, "Our No." must be mapped. Click on each dropdown to adjust the mapping if needed.'}
              type="info"
              showIcon
              style={{ marginBottom: spacing.lg }}
            />

            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: previewData.headers.length * 150 }}>
                <thead>
                  <tr>
                    {previewData.headers.map((header, index) => {
                      const mappedField = getMappedFieldForColumn(index);
                      const isAssigned = mappedField !== null;
                      return (
                        <th
                          key={index}
                          style={{
                            padding: spacing.md,
                            border: `1px solid ${colors.border.default}`,
                            backgroundColor: isAssigned ? '#e6fffb' : colors.background.secondary,
                            minWidth: 150,
                          }}
                        >
                          <div style={{ marginBottom: spacing.sm }}>
                            <Select
                              style={{ width: '100%' }}
                              placeholder="Select field..."
                              value={mappedField || undefined}
                              onChange={(value) => value ? handleFieldAssignment(value as keyof ColumnMapping, index + 1) : handleFieldClear(index + 1)}
                              allowClear
                            >
                              {FIELD_OPTIONS.map(option => (
                                <Option key={option.value} value={option.value}>
                                  {option.label}
                                </Option>
                              ))}
                            </Select>
                          </div>
                          <Text strong style={{ fontSize: 12 }}>{header}</Text>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {previewData.sampleRows.map((row, rowIndex) => (
                    <tr key={rowIndex}>
                      {row.map((cell, cellIndex) => (
                        <td
                          key={cellIndex}
                          style={{
                            padding: spacing.sm,
                            border: `1px solid ${colors.border.default}`,
                            fontSize: 12,
                            maxWidth: 150,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                          title={cell}
                        >
                          {cell}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div style={{ marginTop: spacing.lg }}>
              <Text type="secondary">
                Showing {previewData.sampleRows.length} of {previewData.totalDataRows} data rows
                {previewData.headerRowIndex > 0 && ` (header detected at row ${previewData.headerRowIndex})`}
              </Text>
            </div>
          </Card>
        )}

        {currentStep === 'results' && result && (
          <>
            <Row gutter={16} style={{ marginBottom: spacing.lg }}>
              <Col xs={12} sm={8} md={4}>
                <Card size="small">
                  <Statistic
                    title="Total Processed"
                    value={result.summary.totalProcessed}
                    valueStyle={{ color: colors.text.primary }}
                  />
                </Card>
              </Col>
              <Col xs={12} sm={8} md={4}>
                <Card size="small">
                  <Statistic
                    title="Match Rate"
                    value={result.summary.matchRate}
                    suffix="%"
                    valueStyle={{ color: result.summary.matchRate >= 80 ? '#52c41a' : '#faad14' }}
                  />
                </Card>
              </Col>
              <Col xs={12} sm={8} md={4}>
                <Card size="small">
                  <Statistic
                    title="Found in DB"
                    value={result.summary.foundInDatabase}
                    valueStyle={{ color: '#52c41a' }}
                  />
                </Card>
              </Col>
              <Col xs={12} sm={8} md={4}>
                <Card size="small">
                  <Statistic
                    title="Not Found"
                    value={result.summary.notFoundInDatabase}
                    valueStyle={{ color: result.summary.notFoundInDatabase > 0 ? '#ff4d4f' : colors.text.primary }}
                  />
                </Card>
              </Col>
              <Col xs={12} sm={8} md={4}>
                <Card size="small">
                  <Statistic
                    title="Discrepancies"
                    value={result.summary.withDiscrepancies}
                    valueStyle={{ color: result.summary.withDiscrepancies > 0 ? '#faad14' : '#52c41a' }}
                  />
                </Card>
              </Col>
              <Col xs={12} sm={8} md={4}>
                <Card size="small">
                  <Statistic
                    title="One-Sided"
                    value={result.summary.oneSidedUpdates}
                    valueStyle={{ color: result.summary.oneSidedUpdates > 0 ? '#1890ff' : colors.text.primary }}
                  />
                </Card>
              </Col>
            </Row>
            
            {result.format && (
              <Alert
                message={`Detected format: ${result.format === 'almond' ? 'Almond (13-column)' : 'Walnut (12-column)'}`}
                type="info"
                showIcon
                style={{ marginBottom: spacing.lg }}
              />
            )}

            <Card>
              <Table
                columns={columns}
                dataSource={result.rows}
                rowKey="ourNo"
                scroll={{ x: 1600 }}
                size="small"
                pagination={{
                  pageSize: 50,
                  showSizeChanger: true,
                  pageSizeOptions: ['25', '50', '100'],
                  showTotal: (total) => `${total} rows`,
                }}
                rowClassName={(record) => {
                  if (record.status === 'Not Found in Database') return 'row-not-found';
                  if (record.discrepancies.length > 0) return 'row-discrepancy';
                  return 'row-matched';
                }}
              />
            </Card>
          </>
        )}

        <DiscrepancyModal
          row={discrepancyModalRow}
          resolutions={discrepancyModalRow ? selectedResolutions.get(discrepancyModalRow.ourNo) : undefined}
          onResolve={(field, source) => {
            if (discrepancyModalRow) setResolution(discrepancyModalRow.ourNo, field, source);
          }}
          onClose={() => setDiscrepancyModalRow(null)}
        />
      </div>

      <style>{`
        .row-not-found td { background-color: #fff2f0 !important; }
        .row-not-found:hover td { background-color: #ffccc7 !important; }
        .row-discrepancy td { background-color: #fffbe6 !important; }
        .row-discrepancy:hover td { background-color: #fff1b8 !important; }
        .row-matched td { background-color: #f6ffed !important; }
        .row-matched:hover td { background-color: #d9f7be !important; }
      `}</style>
    </div>
  );
};

const ThreeWayCell: React.FC<{
  label: string;
  ss: string;
  db: string;
  pc: string;
  discrepancy?: Discrepancy;
}> = ({ ss, db, pc, discrepancy }) => {
  if (!ss && !db && !pc) return <Text type="secondary">-</Text>;

  if (!discrepancy) {
    return <Text>{ss || db || pc || '-'}</Text>;
  }

  const getTagStyle = (type: DiscrepancyType, source: 'ss' | 'db') => {
    if (type === 'conflict') {
      return source === 'ss' ? 'blue' : 'green';
    }
    if (type === 'ss_only') return 'blue';
    if (type === 'db_only') return 'green';
    return 'default';
  };

  return (
    <Space direction="vertical" size={0} style={{ fontSize: 11 }}>
      {ss && (
        <div>
          <Tag color={getTagStyle(discrepancy.type, 'ss')} style={{ fontSize: 10, padding: '0 4px' }}>
            SS{discrepancy.type === 'ss_only' ? ' only' : ''}
          </Tag> {ss}
        </div>
      )}
      {db && (
        <div>
          <Tag color={getTagStyle(discrepancy.type, 'db')} style={{ fontSize: 10, padding: '0 4px' }}>
            DB{discrepancy.type === 'db_only' ? ' only' : ''}
          </Tag> {db}
        </div>
      )}
      {pc && <div><Tag color="purple" style={{ fontSize: 10, padding: '0 4px' }}>PC</Tag> {pc}</div>}
    </Space>
  );
};

const DiscrepancyModal: React.FC<{
  row: ReconciliationRow | null;
  resolutions?: Map<string, 'spreadsheet' | 'database' | 'portcast'>;
  onResolve: (field: string, source: 'spreadsheet' | 'database' | 'portcast') => void;
  onClose: () => void;
}> = ({ row, resolutions, onResolve, onClose }) => {
  if (!row) return null;

  return (
    <Modal
      title={`Resolve Discrepancies: ${row.ourNo}`}
      open={!!row}
      onCancel={onClose}
      footer={[
        <Button key="close" onClick={onClose}>Close</Button>,
      ]}
      width={700}
    >
      <Alert
        message={`${row.discrepancies.length} discrepancies found for ${row.ourNo} (${row.rimtradeRef})`}
        type="warning"
        showIcon
        style={{ marginBottom: 16 }}
      />

      {row.discrepancies.map((disc) => {
        const currentSelection = resolutions?.get(disc.field);
        const isReadOnly = disc.field === 'Start' || disc.field === 'End';
        
        const getTypeLabel = (type: DiscrepancyType) => {
          if (type === 'ss_only') return <Tag color="blue">SS Only</Tag>;
          if (type === 'db_only') return <Tag color="green">DB Only</Tag>;
          return <Tag color="orange">Conflict</Tag>;
        };

        return (
          <Card key={disc.field} size="small" style={{ marginBottom: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <Text strong>{disc.field}</Text>
              {getTypeLabel(disc.type)}
            </div>
            
            {isReadOnly ? (
              <Alert
                message="Period fields are read-only and will not be updated on export"
                type="info"
                showIcon
                style={{ marginBottom: 8 }}
              />
            ) : null}
            
            <Radio.Group
              value={currentSelection}
              onChange={(e) => onResolve(disc.field, e.target.value)}
              disabled={isReadOnly}
            >
              <Space direction="vertical" style={{ width: '100%' }}>
                {disc.spreadsheet && (
                  <Radio value="spreadsheet">
                    <Tag color="blue">Spreadsheet</Tag> {disc.spreadsheet}
                  </Radio>
                )}
                {disc.database && (
                  <Radio value="database">
                    <Tag color="green">Database</Tag> {disc.database}
                  </Radio>
                )}
                {disc.portcast && (
                  <Radio value="portcast">
                    <Tag color="purple">Portcast</Tag> {disc.portcast}
                    <Text type="secondary" style={{ marginLeft: 8, fontSize: 11 }}>
                      (source of truth)
                    </Text>
                  </Radio>
                )}
              </Space>
            </Radio.Group>
          </Card>
        );
      })}
    </Modal>
  );
};

export default ShinyeiReconciliation;
